import React, { FC, useState } from 'react';

import moment from 'moment';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { useRouter } from 'next/router';

import { VehicleOfficeSearchViewModel } from '@api/index';
import TimePickerComp from '@shared/TimePicker';
import SearchCarRentalButton from '@domain/carRental/searchBar/home/searchCarRentalButton/index';
import STATIC_ROUTES from '@data/staticRoutes.json';
import {
  formatMomentDate,
  getNDaysLater,
  HOTEL_SEARCH_DATE_FORMAT,
} from '@utils/dateUtil';
import Checkbox from '@components/Checkbox';

import DateSelection from './dateSelection';
import {
  StyledContent,
  StyledLayout,
  DateWrapper,
  CheckboxWrapper,
} from './styled';
import CarRentalSelection from './carRentalSelection';
import useCarRentalSearchBar from './context';

interface ICarRentalSearch {
  alignVertical?: boolean;
}

const CarRentalHomeSearchBarContent: FC<ICarRentalSearch> = ({
  alignVertical,
}) => {
  const [isSubmited, setIsSubmited] = useState(false);

  const { push } = useRouter();
  const screen = useBreakpoint();
  const searchBarContext = useCarRentalSearchBar();

  const handleSearch = () => {
    setIsSubmited(true);
    if (!searchBarContext.state.departure || !searchBarContext.state.arrival) {
      return;
    }

    push({
      pathname: STATIC_ROUTES.carRentalSearchPathPrefix,
      query: {
        pickUpLocation: searchBarContext.state.departure.code,
        pickUpDate: formatMomentDate(
          searchBarContext.state.pickUpDate,
          HOTEL_SEARCH_DATE_FORMAT
        ),
        pickUpTime: searchBarContext.state.pickUpTime,
        dropOffLocation: searchBarContext.state.arrival.code,
        dropOffTime: searchBarContext.state.dropUpTime,
        dropOffDate: formatMomentDate(
          searchBarContext.state.dropUpDate,
          HOTEL_SEARCH_DATE_FORMAT
        ),
      },
    });
  };

  const isSameDate =
    formatMomentDate(searchBarContext.state.dropUpDate) ===
    formatMomentDate(searchBarContext.state.pickUpDate);

  const handleDropUpTime = (value) => {
    if (
      value?.split(':')[0] < searchBarContext.state.pickUpTime?.split(':')[0] &&
      isSameDate
    ) {
      searchBarContext.updatePickUpTime(value);
      searchBarContext.updateDropUpTime(value);
    } else {
      searchBarContext.updateDropUpTime(value);
    }
  };

  const handlePickUpTime = (value) => {
    searchBarContext.updatePickUpTime(value);
    searchBarContext.updateDropUpTime(value);
  };

  const handleDifferentLocationClick = (isDifferentLocation: boolean) => {
    searchBarContext.updateIsDifferentLocation(isDifferentLocation);
    if (isDifferentLocation) {
      searchBarContext.updateArrival(null);
    } else {
      searchBarContext.updateArrival(searchBarContext.state.departure);
    }
  };

  return (
    <StyledLayout>
      <StyledContent
        direction={screen.xs ? true : false}
        alignVertical={alignVertical}
      >
        <CarRentalSelection
          onChange={(departure: VehicleOfficeSearchViewModel) => {
            searchBarContext.updateDeparture(departure);
            if (!searchBarContext.state.isDifferentLocation) {
              searchBarContext.updateArrival(departure);
            }
          }}
          label='Alış Ofisi'
          isSubmited={isSubmited}
          alignVertical={alignVertical}
        />
        <DateWrapper alignVertical={alignVertical}>
          <DateSelection
            title='Alış Tarihi'
            date={searchBarContext.state.pickUpDate}
            setDate={(value) => {
              searchBarContext.updatePickUpDate(moment(value));
              // when the pick up date is selected, the drop off date must be the same.
              searchBarContext.updateDropUpDate(
                getNDaysLater(moment(value), 1)
              );
            }}
            initialDate={moment()}
          />
          <TimePickerComp
            defaultValue={searchBarContext.state.pickUpTime}
            title='Alış Saati'
            time={searchBarContext.state.pickUpTime}
            onChange={handlePickUpTime}
            containerClassName={`${
              alignVertical ? 'is-bordered' : ''
            } car-rental-time-picker`}
            alignItems='flex-start'
          />
        </DateWrapper>
        <DateWrapper alignVertical={alignVertical}>
          {searchBarContext.state.isDifferentLocation ? (
            <CarRentalSelection
              onChange={(arrival: VehicleOfficeSearchViewModel) => {
                searchBarContext.updateArrival(arrival);
              }}
              label='Teslim Ofisi'
              isSubmited={isSubmited}
              alignVertical={alignVertical}
            />
          ) : (
            <div className='is-bordered' />
          )}
          <DateSelection
            title='Teslim Tarihi'
            date={searchBarContext.state.dropUpDate}
            setDate={(value) => {
              searchBarContext.updateDropUpDate(moment(value));
            }}
            isOutsideRange={(day) => {
              return day.isBefore(
                moment(searchBarContext.state.pickUpDate),
                'day'
              );
            }}
          />
          <TimePickerComp
            title='Teslim Saati'
            defaultValue={searchBarContext.state.dropUpTime}
            onChange={handleDropUpTime}
            time={searchBarContext.state.dropUpTime}
            containerClassName='is-bordered car-rental-time-picker'
            alignItems='flex-start'
          />
        </DateWrapper>

        {screen.xs ? (
          <CheckboxWrapper>
            <Checkbox
              className='checkbox-desktop'
              title='Farklı yerde bırakmak istiyorum'
              checked={searchBarContext.state.isDifferentLocation}
              onChange={handleDifferentLocationClick}
              value={searchBarContext.state.isDifferentLocation}
            />
          </CheckboxWrapper>
        ) : null}

        <SearchCarRentalButton onClick={handleSearch} />
      </StyledContent>
      {!screen.xs ? (
        <CheckboxWrapper>
          <Checkbox
            className='checkbox-desktop'
            title='Farklı yerde bırakmak istiyorum'
            checked={searchBarContext.state.isDifferentLocation}
            onChange={handleDifferentLocationClick}
            value={searchBarContext.state.isDifferentLocation}
          />
        </CheckboxWrapper>
      ) : null}
    </StyledLayout>
  );
};

export default CarRentalHomeSearchBarContent;
